import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { subscriptionsAPI, extractErrorMessage } from '../api';

const TIER_COLORS = {
  trial: '#6C63FF',
  free: '#94A3B8',
  basic: '#3B82F6',
  starter: '#6C63FF',
  growth: '#10B981',
};

const FEATURE_ROWS = [
  { key: 'max_products',       label: 'Products',              format: (v) => v === -1 ? 'Unlimited' : v.toLocaleString() },
  { key: 'max_staff',          label: 'Staff Members',         format: (v) => v === -1 ? 'Unlimited' : v },
  { key: 'max_locations',      label: 'Locations',             format: (v) => v === -1 ? 'Unlimited' : v },
  { key: 'max_monthly_sales',  label: 'Orders / Month',        format: (v) => v === -1 ? 'Unlimited' : v.toLocaleString() },
  { key: 'basic_reports',            label: 'Basic Reports',         type: 'feature' },
  { key: 'export_csv',               label: 'CSV Export',            type: 'feature' },
  { key: 'export_pdf',               label: 'PDF Export',            type: 'feature' },
  { key: 'whatsapp_notifications',   label: 'WhatsApp Notifications', type: 'feature' },
  { key: 'forecast',                 label: 'Demand Forecasting',    type: 'feature' },
  { key: 'auto_restock',             label: 'Auto Restock',          type: 'feature' },
  { key: 'ai_enabled',               label: 'AI Analytics',          type: 'feature' },
  { key: 'campaigns_enabled',        label: 'Offer Campaigns',       type: 'feature' },
  { key: 'rental',                   label: 'Rental Management',     type: 'feature' },
  { key: 'multi_location',           label: 'Multi-Location',        type: 'feature' },
  { key: 'import_enabled',           label: 'Data Import',           type: 'feature' },
  { key: 'api_access',               label: 'API Access',            type: 'feature' },
  { key: 'priority_support',         label: 'Priority Support',      type: 'feature' },
];

export default function Pricing() {
  const { selectedStore } = useStore();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [currentTier, setCurrentTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [error, setError] = useState('');

  const VALID_PLANS = ['basic', 'starter', 'growth'];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // No region param — always USD
        const res = await subscriptionsAPI.getTiers();
        setTiers(res.data);
        if (selectedStore) {
          const subRes = await subscriptionsAPI.getStoreSubscription(selectedStore.id);
          setCurrentTier(subRes.data?.subscription?.tier);
        }
      } catch (e) {
        setError(extractErrorMessage(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedStore]);

  const handleSelect = async (tier) => {
    if (!selectedStore) { navigate('/login'); return; }
    if (!VALID_PLANS.includes(tier.tier)) {
      setError('Invalid plan selected.');
      return;
    }
    setError('');
    setCheckingOut(tier.tier);
    try {
      const res = await subscriptionsAPI.createCheckout(selectedStore.id, tier.tier);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setCheckingOut(null);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div><span>Loading plans…</span>
      </div>
    );
  }

  return (
    <div className="page-container pricing-page">
      <div className="pricing-header">
        <h1 className="pricing-title">Choose Your Plan</h1>
        
        <p className="pricing-currency-note">
          All prices in <strong>USD</strong>. Your bank will handle currency conversion automatically.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Tier Cards */}
      <div className="pricing-cards">
        {tiers.map((tier) => {
          const isCurrent = currentTier === tier.tier;
          const isPopular = tier.tier === 'growth';
          const isBusy = checkingOut === tier.tier;
          return (
            <div
              key={tier.tier}
              className={`pricing-card ${isPopular ? 'pricing-card-popular' : ''} ${isCurrent ? 'pricing-card-current' : ''}`}
              style={{ '--tier-color': TIER_COLORS[tier.tier] }}
            >
              {isPopular && <div className="pricing-popular-badge">Most Popular</div>}
              <div className="pricing-card-name">{tier.name}</div>
              <div className="pricing-card-price">
                <span className="pricing-price-amount">${tier.monthly_price}</span>
                <span className="pricing-price-period">/month USD</span>
              </div>
              <p className="pricing-card-desc">{tier.description}</p>

              <ul className="pricing-card-features">
                <li>
                  {tier.limits.max_products === -1 ? 'Unlimited' : tier.limits.max_products.toLocaleString()} products
                </li>
                <li>
                  {tier.limits.max_staff === -1 ? 'Unlimited' : tier.limits.max_staff} staff members
                </li>
                <li>
                  {tier.limits.max_monthly_sales === -1 ? 'Unlimited' : tier.limits.max_monthly_sales.toLocaleString()} orders/mo
                </li>
                {tier.limits.features?.whatsapp_notifications && <li>WhatsApp notifications</li>}
                {tier.limits.features?.ai_enabled && <li>AI-powered analytics</li>}
                {tier.limits.features?.campaigns_enabled && <li>Offer campaigns</li>}
                {tier.limits.features?.rental && <li>Rental management</li>}
                {tier.limits.features?.forecast && <li>Demand forecasting</li>}
              </ul>

              {isCurrent ? (
                <button className="btn btn-secondary pricing-card-btn" disabled>
                  Current Plan
                </button>
              ) : (
                <button
                  className={`btn ${isPopular ? 'btn-primary' : 'btn-secondary'} pricing-card-btn`}
                  onClick={() => handleSelect(tier)}
                  disabled={isBusy}
                >
                  {isBusy ? 'Redirecting…' : (currentTier ? 'Switch Plan' : 'Get Started')}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Feature Comparison Table */}
      <div className="pricing-comparison">
        <h2 className="pricing-comparison-title">Feature Comparison</h2>
        <div className="table-container">
          <table className="data-table pricing-table">
            <thead>
              <tr>
                <th>Feature</th>
                {tiers.map((t) => (
                  <th key={t.tier} style={{ color: TIER_COLORS[t.tier] }}>{t.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_ROWS.map((row) => (
                <tr key={row.key}>
                  <td className="pricing-feature-name">{row.label}</td>
                  {tiers.map((t) => {
                    if (row.type === 'feature') {
                      const val = t.limits.features?.[row.key];
                      return (
                        <td key={t.tier} className="pricing-feature-cell">
                          {val
                            ? <span className="pricing-check">&#10003;</span>
                            : <span className="pricing-dash">—</span>}
                        </td>
                      );
                    }
                    const val = t.limits[row.key];
                    return (
                      <td key={t.tier} className="pricing-feature-cell">
                        {row.format ? row.format(val) : val}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pricing-legal-footer">
        <p>All prices in USD · Billed monthly · Cancel anytime</p>
        <p>
          <a href="/terms" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>
          <span className="pricing-legal-sep"> · </span>
          <a href="/refund-policy" target="_blank" rel="noopener noreferrer">Refund Policy</a>
        </p>
      </div>
    </div>
  );
}
